import React, { useState, useEffect, useCallback } from "react";
import { FaCalendarAlt, FaChevronLeft, FaChevronRight, FaTimes,
         FaCheckCircle, FaTimesCircle, FaUndo } from "react-icons/fa";
import {useTheme, getT, getBrandColors} from "./ThemeContext";
import PageHeaderIcon from "./components/PageHeaderIcon";
import API_BASE from "./apiConfig";
import { useDateTimeFormat } from "./utils/formatters";
import { PrimaryButton } from "./components/ThemedButton";
import StatusBadge from "./components/StatusBadge";

const BASE = API_BASE;
const FONT = "'Segoe UI', system-ui, sans-serif";
const BLUR = "blur(18px)";

const STATUS_META = {
  pending_payment: { label: "Pending",    tone: "warning" },
  confirmed:       { label: "Confirmed",  tone: "success" },
  checked_in:      { label: "Checked In", tone: "info" },
  completed:       { label: "Completed",  tone: "info" },
  no_show:         { label: "No Show",    tone: "warning" },
};

// Resolves a booking status to its rendered {label, color, bg} using the live
// theme's tone-driven status pill colors (see utils/themeStyles.js).
function statusMeta(status, T) {
  const meta = STATUS_META[status] || { label: status, tone: "neutral" };
  const pill = T.statusPill(meta.tone);
  return { label: meta.label, color: pill.color, bg: pill.background, tone: meta.tone };
}

function isoDate(d) {
  return `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,"0")}-${String(d.getDate()).padStart(2,"0")}`;
}

// ── Booking Drawer ─────────────────────────────────────────────────────────────
function BookingDrawer({ bookingId, T, onClose, onRefresh }) {
  const { fmtDate, fmtTime } = useDateTimeFormat();
  const [booking, setBooking] = useState(null);
  const [loading, setLoading] = useState(true);
  const [acting,  setActing]  = useState(null);
  const [refundAmt, setRefundAmt] = useState("");
  const [refundReason, setRefundReason] = useState("");
  const [showRefund, setShowRefund] = useState(false);

  const load = useCallback(() => {
    setLoading(true);
    fetch(`${BASE}/lb/bookings/${bookingId}`).then(r => r.json())
      .then(d => { setBooking(d); setLoading(false); });
  }, [bookingId]);

  useEffect(() => { load(); }, [load]);

  const action = async (endpoint, body = {}) => {
    setActing(endpoint);
    await fetch(`${BASE}/lb/bookings/${bookingId}/${endpoint}`, {
      method: "POST", headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body),
    });
    setActing(null); load(); onRefresh();
  };

  const refund = async () => {
    if (!refundAmt) return;
    setActing("refund");
    await fetch(`${BASE}/lb/bookings/${bookingId}/refund`, {
      method: "POST", headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ amount: parseFloat(refundAmt), reason: refundReason, refunded_by: "admin" }),
    });
    setActing(null); setShowRefund(false); setRefundAmt(""); setRefundReason(""); load(); onRefresh();
  };

  const SM = booking ? statusMeta(booking.status, T) : { label: "", color: "#6b7280", bg: "rgba(107,114,128,0.18)" };

  return (
    <div style={{ position: "fixed", top: 0, right: 0, bottom: 0, width: 400, zIndex: 500,
                  background: T.glass, backdropFilter: "blur(24px)", WebkitBackdropFilter: "blur(24px)",
                  borderLeft: T.glassBdr, boxShadow: "-8px 0 40px rgba(0,0,0,0.3)",
                  overflowY: "auto", display: "flex", flexDirection: "column" }}>
      <div style={{ padding: "20px 24px", borderBottom: `1px solid ${T.divider}`,
                    display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <div style={{ fontSize: 15, fontWeight: 800, color: T.text, fontFamily: FONT }}>Booking Detail</div>
        <button style={{ background: "none", border: "none", cursor: "pointer", color: T.textMuted }}
          onClick={onClose}><FaTimes size={16} /></button>
      </div>

      {loading ? (
        <div style={{ padding: 40, textAlign: "center", color: T.textMuted, fontFamily: FONT }}>Loading…</div>
      ) : booking ? (
        <div style={{ padding: "20px 24px", flex: 1 }}>
          {/* Ref + Status */}
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 20 }}>
            <div>
              <div style={{ fontSize: 13, fontWeight: 800, color: T.text, fontFamily: FONT }}>{booking.booking_ref}</div>
              <div style={{ fontSize: 12, color: T.textMuted, fontFamily: FONT, marginTop: 2 }}>
                {fmtDate(booking.booking_date)} · {fmtTime(booking.start_time)} – {fmtTime(booking.end_time)}
              </div>
            </div>
            <StatusBadge label={SM.label} tone={SM.tone} style={{ padding: "4px 12px", fontFamily: FONT }} />
          </div>

          {/* Details grid */}
          {[
            ["Client",   booking.client_name],
            ["Email",    booking.client_email],
            ["Lane",     booking.lane_name],
            ["Type",     booking.type_name],
            ["Duration", booking.duration_label],
            ["Amount",   `$${parseFloat(booking.total_amount).toFixed(2)}${booking.is_academy_booking ? " (Academy)" : ""}`],
            ["Refunded", booking.total_refunded > 0 ? `$${parseFloat(booking.total_refunded).toFixed(2)}` : null],
            ["Payment",  booking.stripe_payment_status || (booking.is_academy_booking ? "N/A" : "—")],
            ["Card",     booking.card_last4 ? `${booking.card_brand?.toUpperCase()} ••••${booking.card_last4}` : null],
          ].filter(([, v]) => v).map(([lbl, val]) => (
            <div key={lbl} style={{ display: "flex", justifyContent: "space-between",
                                    padding: "8px 0", borderBottom: `1px solid ${T.divider}` }}>
              <span style={{ fontSize: 12, color: T.textLabel, fontFamily: FONT }}>{lbl}</span>
              <span style={{ fontSize: 13, fontWeight: 600, color: T.text, fontFamily: FONT }}>{val}</span>
            </div>
          ))}

          {booking.notes && (
            <div style={{ marginTop: 14, padding: "10px 14px", borderRadius: 8,
                          background: T.inputBg, fontSize: 12, color: T.textMuted, fontFamily: FONT }}>
              {booking.notes}
            </div>
          )}

          {/* Actions */}
          <div style={{ marginTop: 20, display: "flex", flexWrap: "wrap", gap: 8 }}>
            {booking.status === "confirmed" && (
              <button style={{ padding: "8px 16px", borderRadius: 9, border: "none", cursor: "pointer",
                               background: T.statusPill("info").background, color: T.statusPill("info").color,
                               fontSize: 12, fontWeight: 700, fontFamily: FONT }}
                onClick={() => action("checkin")} disabled={acting}>
                <FaCheckCircle size={11} style={{ marginRight: 5 }} />Check In
              </button>
            )}
            {["confirmed", "checked_in"].includes(booking.status) && (
              <button style={{ padding: "8px 16px", borderRadius: 9, border: "none", cursor: "pointer",
                               background: T.statusPill("info").background, color: T.statusPill("info").color,
                               fontSize: 12, fontWeight: 700, fontFamily: FONT }}
                onClick={() => action("complete")} disabled={acting}>
                Complete
              </button>
            )}
            {!["cancelled","completed"].includes(booking.status) && (
              <button style={{ padding: "8px 16px", borderRadius: 9, border: "none", cursor: "pointer",
                               background: T.statusPill("danger").background, color: T.statusPill("danger").color,
                               fontSize: 12, fontWeight: 700, fontFamily: FONT }}
                onClick={() => action("cancel", { reason: "Admin cancel", cancelled_by: "admin", issue_refund: true })}
                disabled={acting}>
                <FaTimesCircle size={11} style={{ marginRight: 5 }} />Cancel
              </button>
            )}
            {booking.status !== "cancelled" && parseFloat(booking.total_amount) > parseFloat(booking.total_refunded || 0) && (
              <button style={{ padding: "8px 16px", borderRadius: 9, border: "none", cursor: "pointer",
                               background: T.statusPill("warning").background, color: T.statusPill("warning").color,
                               fontSize: 12, fontWeight: 700, fontFamily: FONT }}
                onClick={() => setShowRefund(r => !r)} disabled={acting}>
                <FaUndo size={11} style={{ marginRight: 5 }} />Refund
              </button>
            )}
          </div>

          {showRefund && (
            <div style={{ marginTop: 14, padding: "14px 16px", borderRadius: 10,
                          background: T.inputBg, border: T.glassBdr }}>
              <div style={{ fontSize: 12, fontWeight: 700, color: T.text, fontFamily: FONT, marginBottom: 10 }}>
                Refund (already refunded: ${parseFloat(booking.total_refunded || 0).toFixed(2)} /
                ${parseFloat(booking.total_amount).toFixed(2)})
              </div>
              <input type="number" step="0.01" value={refundAmt}
                onChange={e => setRefundAmt(e.target.value)}
                placeholder={`Max $${(parseFloat(booking.total_amount) - parseFloat(booking.total_refunded || 0)).toFixed(2)}`}
                style={{ padding: "8px 12px", borderRadius: 7, border: `1px solid ${T.inputBdr}`,
                         background: T.inputBg, color: T.inputText, fontSize: 13,
                         fontFamily: FONT, outline: "none", width: "100%", marginBottom: 8 }} />
              <input value={refundReason} onChange={e => setRefundReason(e.target.value)}
                placeholder="Reason (optional)"
                style={{ padding: "8px 12px", borderRadius: 7, border: `1px solid ${T.inputBdr}`,
                         background: T.inputBg, color: T.inputText, fontSize: 13,
                         fontFamily: FONT, outline: "none", width: "100%", marginBottom: 10 }} />
              <PrimaryButton onClick={refund} disabled={acting === "refund"}
                style={{ fontFamily: FONT }}>
                {acting === "refund" ? "Processing…" : "Issue Refund"}
              </PrimaryButton>
            </div>
          )}
        </div>
      ) : null}
    </div>
  );
}

// ── Day View ───────────────────────────────────────────────────────────────────
// Returns the booking on a conflicting lane at the given hour/date, or null
function getConflictBooking(conflicts, bookings, lane_id, isoDate, h) {
  const blocked = (conflicts || {})[String(lane_id)] || [];
  for (const cid of blocked) {
    const found = bookings.find(b =>
      b.lane_id === Number(cid) &&
      (isoDate ? b.booking_date === isoDate : true) &&
      parseInt(b.start_time) <= h &&
      parseInt((b.end_time || "").split(":")[0]) > h
    );
    if (found) return found;
  }
  return null;
}

const BLOCKED_STRIPE_LIGHT = "repeating-linear-gradient(45deg,#f8fafc,#f8fafc 5px,#eef2f7 5px,#eef2f7 10px)";
const BLOCKED_STRIPE_DARK  = "repeating-linear-gradient(45deg,#0d1726,#0d1726 5px,#121e30 5px,#121e30 10px)";

function BlockedCell({ conflictBooking, laneConflict, T, isDark }) {
  const SM = statusMeta(conflictBooking.status, T);
  return (
    <div title={`Blocked — ${laneConflict} is booked (${conflictBooking.client_name || "Academy"})`}
      style={{ background: isDark ? BLOCKED_STRIPE_DARK : BLOCKED_STRIPE_LIGHT,
               borderRadius: 6, padding: "3px 6px", height: "100%",
               display: "flex", flexDirection: "column", alignItems: "center",
               justifyContent: "center", gap: 2, minHeight: 28 }}>
      <div style={{ fontSize: 9, fontWeight: 800, color: SM.color || "#6b7280",
                    textTransform: "uppercase", letterSpacing: "0.05em", fontFamily: FONT }}>
        Blocked
      </div>
      <div style={{ fontSize: 9, color: T.textMuted, fontFamily: FONT, textAlign: "center" }}>
        {laneConflict}
      </div>
    </div>
  );
}

function DayView({ date, lanes, bookings, conflicts, T, isDark, onSelect }) {
  const hours = Array.from({ length: 16 }, (_, i) => i + 6); // 06:00–22:00
  const bookForLaneHour = (lane_id, h) =>
    bookings.filter(b => b.lane_id === lane_id &&
      parseInt(b.start_time) <= h && parseInt(b.end_time?.split(":")[0]) > h);

  return (
    <div style={{ overflowX: "auto" }}>
      <table style={{ borderCollapse: "collapse", minWidth: 600 }}>
        <thead>
          <tr>
            <th style={{ width: 56, padding: "8px 10px", fontSize: 11,
                         fontWeight: 800, color: T.textLabel, fontFamily: FONT,
                         borderBottom: `1px solid ${T.divider}` }}>Time</th>
            {lanes.map(l => (
              <th key={l.lane_id} style={{ padding: "8px 12px", fontSize: 12, fontWeight: 800,
                                           color: T.text, fontFamily: FONT, textAlign: "center",
                                           borderBottom: `1px solid ${T.divider}`, minWidth: 120 }}>
                <div style={{ display: "flex", alignItems: "center", gap: 6, justifyContent: "center" }}>
                  <div style={{ width: 8, height: 8, borderRadius: 2, background: l.color }} />
                  {l.lane_name}
                </div>
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {hours.map(h => (
            <tr key={h}>
              <td style={{ padding: "6px 10px", fontSize: 11, color: T.textMuted,
                           fontFamily: FONT, borderBottom: `1px solid ${T.divider}`, whiteSpace: "nowrap" }}>
                {h % 12 || 12}{h < 12 ? "am" : "pm"}
              </td>
              {lanes.map(l => {
                const bs = bookForLaneHour(l.lane_id, h);
                if (bs.length === 0) {
                  const cb = getConflictBooking(conflicts, bookings, l.lane_id, null, h);
                  const srcLane = cb ? lanes.find(x => x.lane_id === cb.lane_id) : null;
                  if (cb && srcLane) {
                    return (
                      <td key={l.lane_id} style={{ padding: "4px 6px", verticalAlign: "top",
                                                   borderBottom: `1px solid ${T.divider}`,
                                                   borderLeft: `1px solid ${T.divider}`,
                                                   cursor: "not-allowed" }}>
                        <BlockedCell conflictBooking={cb} laneConflict={srcLane.lane_name} T={T} isDark={isDark} />
                      </td>
                    );
                  }
                }
                return (
                  <td key={l.lane_id} style={{ padding: "4px 6px", verticalAlign: "top",
                                               borderBottom: `1px solid ${T.divider}`,
                                               borderLeft: `1px solid ${T.divider}` }}>
                    {bs.map(b => {
                      const SM = statusMeta(b.status, T);
                      return (
                        <div key={b.booking_id} onClick={() => onSelect(b.booking_id)}
                          style={{ background: SM.bg, color: SM.color, borderRadius: 6, padding: "4px 8px",
                                   fontSize: 11, fontWeight: 700, fontFamily: FONT, cursor: "pointer",
                                   marginBottom: 2, borderLeft: `3px solid ${SM.color}` }}>
                          {b.client_name?.split(" ")[0]}
                        </div>
                      );
                    })}
                  </td>
                );
              })}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// ── Week View ──────────────────────────────────────────────────────────────────
function WeekView({ startDate, lanes, bookings, conflicts, T, isDark, onSelect }) {
  const { fmtTime } = useDateTimeFormat();
  const days = Array.from({ length: 7 }, (_, i) => {
    const d = new Date(startDate);
    d.setDate(d.getDate() + i);
    return d;
  });
  const DAYS = ["Sun","Mon","Tue","Wed","Thu","Fri","Sat"];

  // For week view, a cell is "conflict-blocked" on a given date if ANY booking on
  // a conflicting lane exists that day (we don't have per-hour data here, so shade the whole cell).
  const getConflictForDate = (lane_id, iso) => {
    const blocked = (conflicts || {})[String(lane_id)] || [];
    for (const cid of blocked) {
      const found = bookings.find(b => b.lane_id === Number(cid) && b.booking_date === iso);
      if (found) return found;
    }
    return null;
  };

  return (
    <div style={{ overflowX: "auto" }}>
      <table style={{ borderCollapse: "collapse", width: "100%", minWidth: 700 }}>
        <thead>
          <tr>
            <th style={{ width: 100, padding: "8px 10px", fontSize: 11, fontWeight: 800,
                         color: T.textLabel, fontFamily: FONT, borderBottom: `1px solid ${T.divider}` }}>Lane</th>
            {days.map((d, i) => {
              const isToday = isoDate(d) === isoDate(new Date());
              return (
                <th key={i} style={{ padding: "8px 10px", fontSize: 12, fontWeight: 800,
                                     color: isToday ? "#ffd700" : T.text, fontFamily: FONT,
                                     textAlign: "center", borderBottom: `1px solid ${T.divider}`,
                                     minWidth: 100 }}>
                  {DAYS[d.getDay()]} {d.getDate()}
                </th>
              );
            })}
          </tr>
        </thead>
        <tbody>
          {lanes.map(l => (
            <tr key={l.lane_id}>
              <td style={{ padding: "8px 10px", fontSize: 12, fontWeight: 700, color: T.text,
                           fontFamily: FONT, borderBottom: `1px solid ${T.divider}` }}>
                <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                  <div style={{ width: 8, height: 8, borderRadius: 2, background: l.color }} />
                  {l.lane_name}
                </div>
              </td>
              {days.map((d, i) => {
                const iso = isoDate(d);
                const bs  = bookings.filter(b => b.booking_date === iso && b.lane_id === l.lane_id);

                // Show own bookings first; if empty, check for conflict blocks
                if (bs.length === 0) {
                  const cb = getConflictForDate(l.lane_id, iso);
                  const srcLane = cb ? lanes.find(x => x.lane_id === cb.lane_id) : null;
                  if (cb && srcLane) {
                    return (
                      <td key={i} style={{ padding: "4px 6px", verticalAlign: "top",
                                           borderBottom: `1px solid ${T.divider}`,
                                           borderLeft: `1px solid ${T.divider}`,
                                           background: isDark ? BLOCKED_STRIPE_DARK : BLOCKED_STRIPE_LIGHT,
                                           cursor: "not-allowed" }}>
                        <div title={`Blocked — ${srcLane.lane_name} is booked (${cb.client_name || "Academy"})`}
                          style={{ display: "flex", flexDirection: "column", alignItems: "center",
                                   justifyContent: "center", minHeight: 36, gap: 2 }}>
                          <div style={{ fontSize: 9, fontWeight: 800, color: "#6b7280",
                                        textTransform: "uppercase", fontFamily: FONT }}>Blocked</div>
                          <div style={{ fontSize: 9, color: T.textMuted, fontFamily: FONT }}>
                            {srcLane.lane_name}
                          </div>
                        </div>
                      </td>
                    );
                  }
                }

                return (
                  <td key={i} style={{ padding: "6px 6px", verticalAlign: "top",
                                       borderBottom: `1px solid ${T.divider}`,
                                       borderLeft: `1px solid ${T.divider}` }}>
                    {bs.map(b => {
                      const SM = statusMeta(b.status, T);
                      return (
                        <div key={b.booking_id} onClick={() => onSelect(b.booking_id)}
                          style={{ background: SM.bg, color: SM.color, borderRadius: 6, padding: "4px 8px",
                                   fontSize: 11, fontWeight: 700, fontFamily: FONT,
                                   cursor: "pointer", marginBottom: 4,
                                   borderLeft: `3px solid ${SM.color}` }}>
                          <div>{b.client_name?.split(" ")[0]}</div>
                          <div style={{ fontSize: 9, opacity: 0.8 }}>{fmtTime(b.start_time)}</div>
                        </div>
                      );
                    })}
                  </td>
                );
              })}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// ── Month View ─────────────────────────────────────────────────────────────────
function MonthView({ year, month, bookings, T, onSelect }) {
  const first = new Date(year, month - 1, 1).getDay();
  const days  = new Date(year, month, 0).getDate();
  const weeks = []; let week = [];
  for (let i = 0; i < first; i++) week.push(null);
  for (let d = 1; d <= days; d++) {
    week.push(d);
    if (week.length === 7) { weeks.push(week); week = []; }
  }
  if (week.length) { while (week.length < 7) week.push(null); weeks.push(week); }

  const DAYS = ["Sun","Mon","Tue","Wed","Thu","Fri","Sat"];

  return (
    <table style={{ borderCollapse: "collapse", width: "100%" }}>
      <thead>
        <tr>{DAYS.map(d => (
          <th key={d} style={{ padding: "10px 8px", fontSize: 11, fontWeight: 800,
                               color: T.textLabel, fontFamily: FONT, textAlign: "center",
                               borderBottom: `1px solid ${T.divider}` }}>{d}</th>
        ))}</tr>
      </thead>
      <tbody>
        {weeks.map((week, wi) => (
          <tr key={wi}>
            {week.map((d, di) => {
              const iso = d ? `${year}-${String(month).padStart(2,"0")}-${String(d).padStart(2,"0")}` : null;
              const dayBookings = d ? bookings.filter(b => b.booking_date === iso) : [];
              const isToday = iso === isoDate(new Date());
              return (
                <td key={di} style={{ padding: "6px 6px", verticalAlign: "top", height: 80,
                                      borderBottom: `1px solid ${T.divider}`,
                                      borderLeft: di > 0 ? `1px solid ${T.divider}` : "none" }}>
                  {d && (
                    <>
                      <div style={{ fontSize: 12, fontWeight: isToday ? 900 : 600,
                                    color: isToday ? "#ffd700" : T.textLabel, fontFamily: FONT,
                                    marginBottom: 4 }}>{d}</div>
                      {dayBookings.slice(0, 3).map(b => {
                        const SM = statusMeta(b.status, T);
                        return (
                          <div key={b.booking_id} onClick={() => onSelect(b.booking_id)}
                            style={{ fontSize: 10, fontWeight: 700, fontFamily: FONT, cursor: "pointer",
                                     background: SM.bg, color: SM.color, borderRadius: 4,
                                     padding: "2px 6px", marginBottom: 2, overflow: "hidden",
                                     textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                            {b.client_name?.split(" ")[0]} · {b.lane_name}
                          </div>
                        );
                      })}
                      {dayBookings.length > 3 && (
                        <div style={{ fontSize: 10, color: T.textMuted, fontFamily: FONT }}>
                          +{dayBookings.length - 3} more
                        </div>
                      )}
                    </>
                  )}
                </td>
              );
            })}
          </tr>
        ))}
      </tbody>
    </table>
  );
}

// ── Main Export ────────────────────────────────────────────────────────────────
export default function LaneBookingCalendar({ currentUser }) {
  const { isDark } = useTheme();
  const T = getT(isDark);

  const [facilities,  setFacilities]  = useState([]);
  const [selFac,      setSelFac]      = useState(null);
  const [view,        setView]        = useState("week");
  const [viewDate,    setViewDate]    = useState(new Date());
  const [calData,     setCalData]     = useState({ lanes: [], bookings: [] });
  const [loading,     setLoading]     = useState(false);
  const [selBooking,  setSelBooking]  = useState(null);

  useEffect(() => {
    fetch(`${BASE}/lb/facilities`).then(r => r.json()).then(d => {
      const list = d.facilities || [];
      setFacilities(list);
      if (list.length) setSelFac(list[0].facility_id);
    });
  }, []);

  const load = useCallback(() => {
    if (!selFac) return;
    setLoading(true);
    const iso = isoDate(viewDate);
    fetch(`${BASE}/lb/calendar?facility_id=${selFac}&view_date=${iso}&view=${view}`)
      .then(r => r.json())
      .then(d => { setCalData(d); setLoading(false); })
      .catch(() => setLoading(false));
  }, [selFac, view, viewDate]);

  useEffect(() => { load(); }, [load]);

  const navigate = (dir) => {
    const d = new Date(viewDate);
    if (view === "day")   d.setDate(d.getDate() + dir);
    if (view === "week")  d.setDate(d.getDate() + dir * 7);
    if (view === "month") d.setMonth(d.getMonth() + dir);
    setViewDate(d);
  };

  const MONTHS = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];
  const DAYS   = ["Sun","Mon","Tue","Wed","Thu","Fri","Sat"];

  const viewLabel = () => {
    if (view === "day") return `${DAYS[viewDate.getDay()]}, ${viewDate.getDate()} ${MONTHS[viewDate.getMonth()]} ${viewDate.getFullYear()}`;
    if (view === "week") {
      const start = new Date(viewDate); start.setDate(start.getDate() - start.getDay());
      const end = new Date(start); end.setDate(end.getDate() + 6);
      return `${start.getDate()} ${MONTHS[start.getMonth()]} – ${end.getDate()} ${MONTHS[end.getMonth()]} ${end.getFullYear()}`;
    }
    return `${MONTHS[viewDate.getMonth()]} ${viewDate.getFullYear()}`;
  };

  const card = { background: T.glass, backdropFilter: BLUR, WebkitBackdropFilter: BLUR,
                 border: T.glassBdr, borderRadius: 16, boxShadow: T.glassSh,
                 padding: "20px 24px", boxSizing: "border-box" };
  const tab = (active) => ({
    padding: "7px 16px", borderRadius: 8, border: "none", cursor: "pointer",
    fontSize: 13, fontWeight: 700, fontFamily: FONT,
    background: active ? T.tabActiveBg : T.tabInBg,
    color: active ? T.tabActiveClr : T.tabInClr,
  });

  return (
    <div style={{ padding: "28px 32px", maxWidth: 1200, margin: "0 auto" }}>
      {/* Header */}
      <div style={{ display: "flex", alignItems: "center", gap: 14, marginBottom: 24 }}>
        <PageHeaderIcon icon={<FaCalendarAlt size={20} color={getBrandColors().blueDark} />} />
        <div style={{ flex: 1 }}>
          <div style={{ fontSize: 22, fontWeight: 800, color: T.text, fontFamily: FONT }}>Lane Calendar</div>
          <div style={{ fontSize: 13, color: T.textMuted, fontFamily: FONT, marginTop: 2 }}>
            Facility scheduling overview with color-coded booking status
          </div>
        </div>
      </div>

      {/* Controls */}
      <div style={{ display: "flex", alignItems: "center", gap: 14, marginBottom: 20, flexWrap: "wrap" }}>
        <select value={selFac || ""} onChange={e => setSelFac(Number(e.target.value))}
          style={{ padding: "8px 14px", borderRadius: 8, border: `1px solid ${T.inputBdr}`,
                   background: T.inputBg, color: T.inputText, fontSize: 13, fontFamily: FONT, outline: "none" }}>
          {facilities.map(f => <option key={f.facility_id} value={f.facility_id}>{f.facility_name}</option>)}
        </select>

        <div style={{ display: "flex", gap: 6 }}>
          {["day","week","month"].map(v => (
            <button key={v} style={tab(view === v)} onClick={() => setView(v)}>
              {v.charAt(0).toUpperCase() + v.slice(1)}
            </button>
          ))}
        </div>

        <div style={{ display: "flex", alignItems: "center", gap: 10, marginLeft: "auto" }}>
          <button style={{ ...tab(false), padding: "7px 12px" }} onClick={() => navigate(-1)}>
            <FaChevronLeft size={12} />
          </button>
          <span style={{ fontSize: 14, fontWeight: 700, color: T.text, fontFamily: FONT, minWidth: 220,
                         textAlign: "center" }}>{viewLabel()}</span>
          <button style={{ ...tab(false), padding: "7px 12px" }} onClick={() => navigate(1)}>
            <FaChevronRight size={12} />
          </button>
          <button style={{ ...tab(false), fontSize: 12 }} onClick={() => setViewDate(new Date())}>Today</button>
        </div>
      </div>

      {/* Legend */}
      <div style={{ display: "flex", gap: 14, marginBottom: 16, flexWrap: "wrap" }}>
        {Object.keys(STATUS_META).map(k => {
          const m = statusMeta(k, T);
          return (
            <div key={k} style={{ display: "flex", alignItems: "center", gap: 6 }}>
              <div style={{ width: 10, height: 10, borderRadius: 3, background: m.color }} />
              <span style={{ fontSize: 11, fontWeight: 600, color: T.textMuted, fontFamily: FONT }}>{m.label}</span>
            </div>
          );
        })}
      </div>

      {/* Calendar */}
      <div style={card}>
        {loading ? (
          <div style={{ textAlign: "center", color: T.textMuted, fontFamily: FONT, padding: 40 }}>Loading…</div>
        ) : (
          <>
            {view === "day" && (
              <DayView date={viewDate} lanes={calData.lanes || []}
                bookings={calData.bookings || []} conflicts={calData.conflicts || {}}
                T={T} isDark={isDark} onSelect={setSelBooking} />
            )}
            {view === "week" && (
              <WeekView startDate={new Date(calData.start_date || viewDate)}
                lanes={calData.lanes || []} bookings={calData.bookings || []}
                conflicts={calData.conflicts || {}}
                T={T} isDark={isDark} onSelect={setSelBooking} />
            )}
            {view === "month" && (
              <MonthView year={viewDate.getFullYear()} month={viewDate.getMonth()+1}
                bookings={calData.bookings} T={T} onSelect={setSelBooking} />
            )}
          </>
        )}
      </div>

      {/* Booking drawer */}
      {selBooking && (
        <BookingDrawer bookingId={selBooking} T={T}
          onClose={() => setSelBooking(null)} onRefresh={load} />
      )}
    </div>
  );
}
